import { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { GET_VERSION_CFD_RESULTS } from 'client/queries/getVersionCFDResults'
import { SIMULATION_TYPES } from 'config/cfd'
import { useParams } from 'react-router-dom'
import { CfdStatuses, GetCfdImageResultsQuery } from '~/gql/graphql'
import { filePropsSchema } from '~/components/Panels/CFDPanel/subcomponents/VisualizationToggle'
import { InferOutput, parse } from 'valibot'

const isRelevantImage = (simulationType: string, id: string, fileExtension: string | null | undefined) => {
  switch (simulationType) {
    case SIMULATION_TYPES.radiantHeat:
    case SIMULATION_TYPES.destrat:
    case SIMULATION_TYPES.cooling: {
      const isValidImage = fileExtension === 'png'
      return isValidImage
    }
    case SIMULATION_TYPES.unitHeating: {
      const isValidUnitHeating = id.includes('T_') && fileExtension === 'png'
      return isValidUnitHeating
    }
    default: {
      console.warn('Unexpected internalType: ', simulationType)
      return false
    }
  }
}

/**
 * Matches ids to pull out the turnover percentage value and display it as a decimal.
 * For example, the id "...T_x1-025turnover.png" would have "025" extracted and returned
 * as "0.25". Note that 0% values are represented without a "turnover" suffix: T_x1.png
 */
const parseTurnover = (id: string): string => {
  const turnoverIdRegex = new RegExp(/T_[xyz]\d+(?:inch)?[-.](\d*)/)
  const match = turnoverIdRegex.exec(id)
  const matchedTurnoverValue = match?.[1]
  if (!match || !matchedTurnoverValue) return '0'
  const decimal = parseInt(matchedTurnoverValue) / 100
  return decimal.toString()
}

const isTemperatureImage = (id: string): boolean => {
  const temperatureRegex = new RegExp(/T_[xyz]/)
  return temperatureRegex.test(id)
}
const sortByTurnover = (a: { turnover: string }, b: { turnover: string }) =>
  parseFloat(a.turnover) - parseFloat(b.turnover)

export type GroupedImageUrls = {
  type: string
  status: CfdStatuses
  imageUrls: (Images1D | Images2D)[]
}

export type Images1D = {
  view: string
  images1D: {
    url: string
    fileProps: InferOutput<typeof filePropsSchema>
  }[]
  thumbnail: string
}

export type Images2D = {
  view: string
  images2D: {
    slice: string | number
    airflowImgs: {
      url: string
      turnover: string
    }[]
  }[]
  thumbnail: string
}

const groupImageUrls = (data: GetCfdImageResultsQuery): GroupedImageUrls[] => {
  if (!data.Version) throw new Error('expected non-null version')

  const nonNullCfdResults = data.Version.cfd.filter(cfd => !!cfd)
  return nonNullCfdResults.map(({ URLs, status, internalType }) => {
    const imageUrls1D = new Map<
      string,
      {
        url: string
        fileProps: InferOutput<typeof filePropsSchema>
      }[]
    >()
    const imageUrls2D = new Map<string, Map<string | number, { url: string; turnover: string }[]>>()

    for (const url of URLs) {
      if (!isRelevantImage(internalType, url.id, url.fileExtension)) continue

      const fileProps = parse(filePropsSchema, url.fileProps)

      if (internalType === SIMULATION_TYPES.radiantHeat) {
        const type = url.id.includes('intensity_zNormal_iso') ? 'Plan' : 'Overhead'
        imageUrls1D.set(type, [{ ...url, fileProps }])
      } else if (internalType === SIMULATION_TYPES.unitHeating) {
        if (!url.id.includes('turnover')) continue

        const view = `${fileProps.type}_temperature`

        const roomSlices = imageUrls2D.get(view) ?? new Map<string | number, { url: string; turnover: string }[]>()
        const airflowTurnoverImgs = roomSlices.get(fileProps.level) ?? []
        const currentImg = { ...url, turnover: parseTurnover(url.id) }
        roomSlices.set(fileProps.level, [...airflowTurnoverImgs, currentImg])
        imageUrls2D.set(view, roomSlices)
      } else if (!url.id.includes('turnover')) {
        const { id } = url
        const view = isTemperatureImage(id) ? `${fileProps.type}_temperature` : fileProps.type
        const urlCollection = imageUrls1D.get(view) ?? []
        imageUrls1D.set(view, [{ ...url, fileProps }, ...urlCollection])
      }
    }

    const imageUrls: (Images1D | Images2D)[] = []
    for (const [view, images] of imageUrls1D) {
      imageUrls.push({
        view,
        images1D: images.sort((a, b) => +a.fileProps.level - +b.fileProps.level),
        thumbnail: images[0].url,
      })
    }
    for (const [view, images] of imageUrls2D) {
      const unitHeatEntries = Array.from(images.entries())
      const unitHeatImages = unitHeatEntries.map(([slice, unsortedAirflowImgs]) => ({
        slice,
        airflowImgs: unsortedAirflowImgs.sort(sortByTurnover),
      }))
      imageUrls.push({
        view,
        images2D: unitHeatImages,
        thumbnail: unitHeatImages[0].airflowImgs[0].url,
      })
    }

    return { type: internalType, status, imageUrls }
  })
}

export const useCFDImages = () => {
  const { versionId } = useParams()
  const { data, loading, error, ...queryResults } = useQuery(GET_VERSION_CFD_RESULTS, {
    variables: { versionId: versionId! },
    skip: !versionId,
    fetchPolicy: 'network-only',
  })
  return {
    loading,
    error,
    data: useMemo(() => data && groupImageUrls(data), [data]),
    ...queryResults,
  }
}

interface Image {
  id: string
  url: string
}
interface CFDImagesFlat {
  status: string
  goal: string
  images: Image[]
}
type QueryResult =
  | {
      loading: true
      error: undefined
      data: undefined
    }
  | {
      loading: false
      error: Error
      data: undefined
    }
  | {
      loading: false
      error: undefined
      data: CFDImagesFlat[]
    }

export const useCFDImagesFlat = (): QueryResult => {
  const { versionId } = useParams()
  const { data, loading, error } = useQuery(GET_VERSION_CFD_RESULTS, {
    variables: { versionId: versionId! },
    skip: !versionId,
  })
  const formattedData = useMemo(() => {
    const nonNullResults = data?.Version?.cfd.filter(cfdResult => !!cfdResult)
    return nonNullResults?.map(({ URLs, status, internalType }) => {
      const images = URLs.filter(({ fileExtension }) => fileExtension === 'png').map(({ id, url }) => ({
        id,
        url,
      }))
      return {
        status,
        goal: internalType,
        images,
      }
    })
  }, [data])

  return {
    loading,
    error,
    data: formattedData,
  } as QueryResult
}
